import { useEffect, useState } from "react";

import {
  Navigate,
  useLocation,
} from "react-router-dom";

import { useSelector, useDispatch } from "react-redux";

import { openLoginDialog } from "@/modules/auth/store/authSlice";

import { useAuthRedirect } from "@/modules/auth/hooks/useAuthRedirect";

import { Button } from "@/shared/components/ui/button";

import { getMyFeaturePermissions } from "@/modules/team/api/teamMemberFeaturePermissionApi";
import type { TeamMemberFeaturePermission } from "@/modules/team/api/teamMemberFeaturePermissionApi";

import type { RootState, AppDispatch } from "@/store/store";
import type { AuthRole } from "@/modules/auth/types/auth";

interface ProtectedRouteProps {
  children: React.ReactNode;

  requiredRole:
    | "candidate"
    | "hr"
    | "admin"
    | "recruiter"
    | "operations"
    | "manager";

  /**
   * Existing role behavior:
   *
   * requiredRole="hr"
   *   -> HR, Recruiter, Operations, Manager
   *
   * exactRole={true}
   *   -> only the exact required role
   */
  exactRole?: boolean;
}

interface FeaturePermissionState {
  userId: string;
  status: "loading" | "loaded";
  allowedFeatures: Set<string>;
}

const MANAGED_ROLES = [
  "recruiter",
  "operations",
  "manager",
];

const FEATURE_ROUTES = [
  {
    href: "/hr",
    featureKey: "DASHBOARD",
  },
  {
    href: "/hr/candidates",
    featureKey: "CANDIDATES",
  },
  {
    href: "/hr/voice-calls",
    featureKey: "VOICE_CALLS",
  },
  {
    href: "/hr/jobs",
    featureKey: "JOBS",
  },
  {
    href: "/hr/resume-database",
    featureKey: "RESUME_DATABASE",
  },
];

/**
 * Resolves the module that protects the current route.
 * Candidate detail pages inherit Candidates access.
 * Job applicant pages inherit Jobs access.
 *
 * Action-level permissions, such as Interview Scheduling
 * and Hiring Decisions, are enforced separately.
 */
const getRequiredFeatureKey = (
  pathname: string
): string | null => {
  if (pathname === "/hr" || pathname === "/hr/") {
    return "DASHBOARD";
  }

  if (
    pathname === "/hr/candidates" ||
    pathname.startsWith("/hr/candidates/")
  ) {
    return "CANDIDATES";
  }

  if (pathname.startsWith("/hr/voice-calls")) {
    return "VOICE_CALLS";
  }

  if (pathname.startsWith("/hr/jobs")) {
    return "JOBS";
  }

  if (pathname.startsWith("/hr/resume-database")) {
    return "RESUME_DATABASE";
  }

  if (pathname.startsWith("/hr/my-team")) {
    return "TEAM_MANAGEMENT";
  }

  return null;
};

const getFirstAllowedRoute = (
  allowedFeatures: Set<string>
): string | null => {
  const allowedRoute = FEATURE_ROUTES.find((route) =>
    allowedFeatures.has(route.featureKey)
  );

  return allowedRoute?.href ?? null;
};

export default function ProtectedRoute({
  children,
  requiredRole,
  exactRole = false,
}: ProtectedRouteProps) {
  const {
    user,
    loading,
    isAuthenticated,
  } = useSelector((state: RootState) => state.auth);

  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { redirectUser } = useAuthRedirect();

  const normalizedRole = user?.role?.toLowerCase() ?? "";
  const isManagedRole = MANAGED_ROLES.includes(normalizedRole);

  const isHrPortalPath =
    location.pathname === "/hr" ||
    location.pathname.startsWith("/hr/");

  const requiredFeatureKey = getRequiredFeatureKey(location.pathname);

  const [featurePermissionState, setFeaturePermissionState] =
    useState<FeaturePermissionState | null>(null);

  const canAccess = (role: AuthRole) => {
    if (!user || !role) {
      return false;
    }

    const currentRole = user.role?.toLowerCase();

    if (!currentRole) {
      return false;
    }

    if (exactRole) {
      return currentRole === role;
    }

    if (role === "hr") {
      return [
        "hr",
        "recruiter",
        "operations",
        "manager",
      ].includes(currentRole);
    }

    return currentRole === role;
  };

  /**
   * Fetch module permissions for managed team accounts.
   * HR and ADMIN continue to use existing role-based access rules.
   * Failed permission requests grant no module access.
   */
  useEffect(() => {
    if (
      !isAuthenticated ||
      !isManagedRole ||
      !isHrPortalPath ||
      !user?.id
    ) {
      setFeaturePermissionState(null);
      return;
    }

    let cancelled = false;

    setFeaturePermissionState({
      userId: user.id,
      status: "loading",
      allowedFeatures: new Set<string>(),
    });

    const loadPermissions = async () => {
      try {
        // This API returns TeamMemberFeaturePermission[], not
        // an object containing a `permissions` property.
        const permissions: TeamMemberFeaturePermission[] =
          await getMyFeaturePermissions();

        if (cancelled) {
          return;
        }

        const allowedFeatures = new Set<string>(
          permissions
            .filter(
              (permission: TeamMemberFeaturePermission) =>
                permission.permissionLevel === "ALLOWED"
            )
            .map(
              (permission: TeamMemberFeaturePermission) =>
                permission.featureKey
            )
        );

        setFeaturePermissionState({
          userId: user.id,
          status: "loaded",
          allowedFeatures,
        });
      } catch (error) {
        console.error(
          "Unable to load feature permissions:",
          error
        );

        if (!cancelled) {
          setFeaturePermissionState({
            userId: user.id,
            status: "loaded",
            allowedFeatures: new Set<string>(),
          });
        }
      }
    };

    void loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    isManagedRole,
    isHrPortalPath,
    normalizedRole,
    user?.id,
  ]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!isAuthenticated) {
      if (location.pathname === "/") {
        return;
      }

      dispatch(
        openLoginDialog({
          mode: "login",
          requiredRole,
        })
      );
    }
  }, [
    loading,
    isAuthenticated,
    location.pathname,
    requiredRole,
    dispatch,
  ]);

  const userHasRole = canAccess(requiredRole);

  const permissionsBelongToCurrentUser = Boolean(
    user?.id && featurePermissionState?.userId === user.id
  );

  const permissionsLoading =
    isManagedRole &&
    isHrPortalPath &&
    requiredFeatureKey !== null &&
    (
      !permissionsBelongToCurrentUser ||
      featurePermissionState?.status !== "loaded"
    );

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!userHasRole) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-red-600">
            Access Denied
          </h1>

          <p className="mt-2 text-gray-600">
            You are not authorized to access this page.
          </p>

          <Button
            className="mt-6"
            onClick={() => redirectUser(user)}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (permissionsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-slate-500">
          Loading your module permissions...
        </p>
      </div>
    );
  }

  const allowedFeatures: Set<string> =
    permissionsBelongToCurrentUser && featurePermissionState
      ? featurePermissionState.allowedFeatures
      : new Set<string>();

  const isFeatureDenied =
    isManagedRole &&
    requiredFeatureKey !== null &&
    !allowedFeatures.has(requiredFeatureKey);

  if (isFeatureDenied) {
    const fallbackRoute = getFirstAllowedRoute(allowedFeatures);

    if (fallbackRoute) {
      return <Navigate to={fallbackRoute} replace />;
    }

    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-slate-900">
            No modules assigned
          </h1>

          <p className="mt-2 text-sm text-slate-600">
            Your HR Admin has not granted access to any
            available modules. Please contact your HR Admin.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
